import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { GoogleService } from 'src/app/services/google.service';

interface ISampleItem {
	title: string;
	avatar?: IImage;
	text: string;
	routerLink: string;
}

@Component({
	selector: 'mod-account-ready',
	templateUrl: './account-ready.component.html',
	styleUrl: './account-ready.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AccountReadyComponent extends TypeCoreComponent<undefined> {
	private readonly helpdeskRouterLink = computed<string>(() => {
		return super
			.actionRoute((routes) => routes.helpdeskRoutes.conversation.children.form, this.authUser()?.id)
			.getUrl();
	});

	protected readonly sampleData = signal<UserApi.ITrainerSampleData | undefined>(undefined);

	protected readonly sampleItems = computed<ISampleItem[] | undefined>(() => {
		const sampleData = this.sampleData();

		if (!sampleData) {
			return undefined;
		}

		const items: ISampleItem[] = [];

		if (sampleData.clients.length) {
			const client = sampleData.clients[0];
			items.push({
				title: client.system.name,
				avatar: client.avatar,
				text: 'Sample client',
				routerLink: super
					.actionRoute((routes) => routes.trainerClientRoutes.client.children.view, client.id)
					.getUrl()
			});
		}

		if (sampleData.workouts.length) {
			const workout = sampleData.workouts[0];
			items.push({
				title: workout.system.name,
				avatar: workout.avatar?.[0]?.image,
				text: 'Sample workout',
				routerLink: super
					.actionRoute((routes) => routes.workoutRoutes.workout.children.view, workout.id)
					.getUrl()
			});
		}

		if (sampleData.diets.length) {
			const diet = sampleData.diets[0];
			items.push({
				title: diet.system.name,
				avatar: diet.avatar?.[0]?.image,
				text: 'Sample diet',
				routerLink: super.actionRoute((routes) => routes.dietRoutes.diet.children.view, diet.id).getUrl()
			});
		}

		return items;
	});

	protected readonly successCardButtons = computed<IButton[]>(() => {
		const buttons: IButton[] = [
			{
				title: 'Ask a question',
				routerLink: this.helpdeskRouterLink(),
				buttonType: 'basic',
				buttonSize: 'large',
				icon: 'helpdesk'
			}
		];

		return buttons;
	});

	protected readonly dashboardButtons = computed<IButton[]>(() => {
		const buttons: IButton[] = [
			{
				icon: 'rocket',
				title: 'Go to dashboard',
				routerLink: this.dashboardUrl(),
				buttonType: 'primary',
				buttonSize: 'large'
			}
		];

		return buttons;
	});

	constructor(private readonly googleService: GoogleService) {
		super();

		this.dataInit = () => {
			this.initSampleData();
		};
	}

	private initSampleData(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<UserApi.ITrainerSampleData>((builder) =>
					builder.withCustomApiRoute('user', 'getTrainerSampleData').build()
				)
				.pipe(
					map((response) => {
						this.sampleData.set(response.data);
					})
				)
		);
	}
}
